import {TeamTaskPriority, TeamTaskStatus} from "@/core/interfaces/TeamTask";

export interface TaskColumn {
    status: TeamTaskStatus;
    label: string;
}

/**
 * The three columns of the board, in order. Completion is positional: the
 * design shows no "done" checkbox, only a card moved to the last column.
 *
 * The labels are ours: the mockup binds them to data it does not carry, so
 * they could not be read from it. They are the counterpart of the status
 * values stored by the server and must move together with them.
 */
export const TASK_COLUMNS: TaskColumn[] = [
    {status: 'todo', label: 'À faire'},
    {status: 'doing', label: 'En cours'},
    {status: 'done', label: 'Terminé'}
];

export interface TaskPriorityOption {
    value: TeamTaskPriority;
    label: string;
    /** Tailwind classes for the chip. */
    chipClass: string;
    dotClass: string;
}

/**
 * « Moyenne » and its amber chip are read off a capture of the rendered
 * mockup; « Basse » and « Haute » still are ours. The stored values keep their
 * own names — renaming them would cost a data migration for no gain.
 */
export const TASK_PRIORITIES: TaskPriorityOption[] = [
    {value: 'low', label: 'Basse', chipClass: 'bg-gray-100 text-gray-600', dotClass: 'bg-gray-400'},
    {value: 'normal', label: 'Moyenne', chipClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500'},
    {value: 'high', label: 'Haute', chipClass: 'bg-rose-50 text-rose-700', dotClass: 'bg-rose-500'}
];

export const priorityOption = (priority: TeamTaskPriority): TaskPriorityOption =>
    TASK_PRIORITIES.find(option => option.value === priority) ?? TASK_PRIORITIES[1];

export const columnLabel = (status: TeamTaskStatus): string =>
    TASK_COLUMNS.find(column => column.status === status)?.label ?? status;

/**
 * The status one column further, or null on the last one. Drives the
 * "Avancer le statut" chevron, which must produce exactly what a drop does.
 */
export const nextStatus = (status: TeamTaskStatus): TeamTaskStatus | null => {
    const index = TASK_COLUMNS.findIndex(column => column.status === status);

    return TASK_COLUMNS[index + 1]?.status ?? null;
};

/** The status one column back, or null on the first one. */
export const previousStatus = (status: TeamTaskStatus): TeamTaskStatus | null => {
    const index = TASK_COLUMNS.findIndex(column => column.status === status);

    return index > 0 ? TASK_COLUMNS[index - 1].status : null;
};

/** Two-letter initials for an avatar bubble. */
export const initialsOf = (name: string | undefined | null): string =>
    (name ?? '?')
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();
